import { useMemo, useState } from 'react';
import {
  REASSIGN_KEEP,
  REASSIGN_KEEP_LABEL,
  ROLE_LABEL,
  type ReassignKeep,
  type Role,
} from '@ehr/shared';
import { Dd } from '../Dropdown.js';
import { apiErrorText } from '../../lib/api.js';
import { postReassign, type AccessPerson } from '../../lib/contactAccess.js';
import { SaModal, saMessage, type SaMessage } from './SaModal.js';
import type { ShareTarget } from './shareState.js';

/**
 * ehr#730 (SHARE_ACCESS_10-30-V11 Reassign): a new owner for one contact or the ones ticked on the
 * Contacts list, and what the previous owner keeps (Nothing by default, V11 decision 3a). V11's
 * "Bucket" destination is not built: contacts have no buckets in the EHR yet.
 */
export function ReassignModal({
  targets,
  people,
  presetUserId,
  onCancel,
  onDone,
}: {
  targets: ShareTarget[];
  people: AccessPerson[];
  presetUserId?: string | null;
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const [userId, setUserId] = useState(presetUserId ?? '');
  const [keep, setKeep] = useState<ReassignKeep>('none');
  const [msg, setMsg] = useState<SaMessage | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);
  const owners = useMemo(
    () => new Set(targets.map((t) => t.ownerUserId).filter((x): x is string => !!x)),
    [targets],
  );
  const ownerNames = [
    ...new Set(
      targets.map((t) =>
        t.ownerUserId
          ? (people.find((p) => p.id === t.ownerUserId)?.name ?? 'Someone')
          : 'Unassigned',
      ),
    ),
  ];
  const options = people
    .filter((p) => !(targets.length === 1 && owners.has(p.id)))
    .map((p) => ({ value: p.id, label: `${p.name} · ${ROLE_LABEL[p.role as Role] ?? p.role}` }));

  const confirm = () => {
    if (!userId) {
      setInvalid(true);
      setMsg(saMessage('Pick a user', true));
      return;
    }
    setSaving(true);
    postReassign({ contactIds: targets.map((t) => t.id), ownerUserId: userId, keep })
      .then(() => onDone(`Reassigned to ${people.find((p) => p.id === userId)?.name ?? 'them'}`))
      .catch((e: unknown) => {
        setSaving(false);
        setMsg(saMessage(apiErrorText(e, 'Could not reassign. Try again.'), true));
      });
  };

  return (
    <SaModal
      title="Reassign"
      onClose={onCancel}
      msg={msg}
      onMsgDone={() => setMsg(null)}
      foot={{
        right: (
          <>
            <button type="button" className="btn" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={confirm}
              disabled={saving}
              id="acs-reassign-confirm"
            >
              {saving ? 'Saving…' : 'Reassign'}
            </button>
          </>
        ),
      }}
    >
      <div className="acs-field">
        <div className="acs-field-label">{targets.length === 1 ? 'Contact' : 'Contacts'}</div>
        <div className="acs-nm">{targets.map((t) => t.name).join(', ')}</div>
      </div>
      <div className="acs-field">
        <div className="acs-field-label">Current owner</div>
        <div>{ownerNames.join(', ')}</div>
      </div>
      <div className="acs-field">
        <div className="acs-field-label">Reassign to</div>
        <Dd
          value={userId}
          options={options}
          placeholder="Pick a user"
          ariaLabel="Reassign to"
          className={`acs-dd${invalid ? ' invalid' : ''}`}
          floating
          onChange={(v) => {
            setUserId(v);
            setInvalid(false);
          }}
        />
      </div>
      <div className="acs-field">
        <div className="acs-field-label">Previous owner keeps</div>
        <Dd
          value={keep}
          searchable={false}
          ariaLabel="Previous owner keeps"
          className="acs-dd"
          floating
          options={REASSIGN_KEEP.map((k) => ({ value: k, label: REASSIGN_KEEP_LABEL[k] }))}
          onChange={(v) => setKeep(v as ReassignKeep)}
        />
      </div>
    </SaModal>
  );
}
